/**
 * Utility functions for handling form validation errors across the application
 */

/**
 * Scrolls to the first field with an error and focuses it with enhanced behavior
 * @param errors - Field errors object from API response
 * @param fieldRefs - React ref object containing field references
 * @param showToast - Optional function to show toast message
 */
export const scrollToFirstError = (
  errors: Record<string, string[]>, 
  fieldRefs: React.MutableRefObject<Record<string, HTMLElement | null>>,
  showToast?: (message: string, severity: 'error' | 'warning' | 'info' | 'success') => void
) => {
  const firstErrorField = Object.keys(errors)[0];
  const errorCount = Object.keys(errors).length;
  
  if (firstErrorField && fieldRefs.current[firstErrorField]) {
    // Enhanced scrolling with better positioning
    fieldRefs.current[firstErrorField]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });
    
    // Add visual highlight effect
    const element = fieldRefs.current[firstErrorField];
    if (element) {
      // Add a temporary highlight effect
      element.style.boxShadow = '0 0 0 3px rgba(255, 107, 107, 0.3)';
      element.style.transition = 'box-shadow 0.3s ease-in-out';
      
      setTimeout(() => {
        element.style.boxShadow = '';
        element.style.transition = '';
      }, 2000);
      
      // Focus the field with improved timing
      setTimeout(() => {
        const inputElement = element.querySelector('input, textarea, select') as HTMLInputElement;
        if (inputElement && inputElement.focus) {
          inputElement.focus();
        }
      }, 500);
    }
    
    // Show informative toast message
    if (showToast) {
      const firstErrorMessage = errors[firstErrorField][0];
      if (errorCount === 1) {
        showToast(`Error: ${firstErrorMessage}`, 'error');
      } else {
        showToast(`Found ${errorCount} validation errors. Please check the highlighted fields.`, 'error');
      }
    }
  }
};

/**
 * Creates a change handler that clears field errors when user starts typing
 * @param setFormData - Function to update form data
 * @param setFieldErrors - Function to update field errors
 * @returns Change handler function
 */
export const createFieldChangeHandler = <T extends Record<string, any>>(
  setFormData: React.Dispatch<React.SetStateAction<T>>,
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
) => {
  return (field: keyof T, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    setFieldErrors(prev => {
      if (prev[field as string]) {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      }
      return prev;
    });
  };
};

/**
 * Handles API error responses with proper 422 validation error parsing
 * @param error - Axios error object
 * @param setFieldErrors - Function to update field errors
 * @param setGeneralError - Function to update general error message
 * @param fieldRefs - React ref object containing field references
 * @param defaultMessage - Default error message for non-validation errors
 * @param showToast - Optional function to show toast message
 */
export const handleApiError = (
  error: any,
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
  setGeneralError: (message: string) => void,
  fieldRefs: React.MutableRefObject<Record<string, HTMLElement | null>>,
  defaultMessage: string = 'An error occurred. Please try again.',
  showToast?: (message: string, severity: 'error' | 'warning' | 'info' | 'success') => void
) => {
  console.error('API Error:', error);
  
  // Handle 422 validation errors
  if (error.response?.status === 422 && error.response?.data?.errors) {
    const apiErrors = error.response.data.errors;
    setFieldErrors(apiErrors);
    scrollToFirstError(apiErrors, fieldRefs, showToast);
    setGeneralError('Please correct the errors below and try again.');
  } else {
    // Handle other errors
    const message = error.response?.data?.message || defaultMessage;
    setGeneralError(message);
    if (showToast) {
      showToast(message, 'error');
    }
  }
};

/**
 * Gets the first error message for a field, or returns empty string if no error
 * @param fieldErrors - Field errors object
 * @param fieldName - Name of the field to check
 * @returns First error message or empty string
 */
export const getFieldError = (
  fieldErrors: Record<string, string[]>, 
  fieldName: string
): string => {
  return fieldErrors[fieldName] ? fieldErrors[fieldName][0] : '';
};

/**
 * Checks if a field has an error
 * @param fieldErrors - Field errors object
 * @param fieldName - Name of the field to check
 * @returns Boolean indicating if field has error
 */
export const hasFieldError = (
  fieldErrors: Record<string, string[]>, 
  fieldName: string
): boolean => {
  return !!fieldErrors[fieldName];
};
